import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import FontSizeSelector from '../components/FontSizeSelector';
import fitzaaLogo from '../assets/appIcon.png';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';

const LoginScreen: React.FC = () => {
  const { login, loginWithGoogle, loginWithAzureAD, signUp, isLoading } = useAuth();
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [authLoading, setAuthLoading] = useState<'google' | 'azure' | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const returnUrl = searchParams.get('returnUrl');

  // Track mouse position for parallax effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    try {
      if (isSignUp) {
        await signUp(email, password, displayName);
      } else {
        await login(email, password);
      }

      // Redirect to return URL or home page after successful login
      if (returnUrl) {
        // Use setTimeout to ensure auth state is updated before navigation
        setTimeout(() => {
          navigate(returnUrl);
        }, 100);
      } else {
        navigate('/home');
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Authentication failed');
    }
  };

  const handleGoogleSignIn = async () => {
    setAuthLoading('google');
    setErrorMessage('');
    try {
      await loginWithGoogle();

      // Redirect to return URL or home page after successful login
      if (returnUrl) {
        // Use setTimeout to ensure auth state is updated before navigation
        setTimeout(() => {
          navigate(returnUrl);
        }, 100);
      } else {
        navigate('/home');
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Google sign-in failed');
    } finally {
      setAuthLoading(null);
    }
  };

  const handleAzureADSignIn = async () => {
    setAuthLoading('azure');
    setErrorMessage('');
    try {
      await loginWithAzureAD();

      // Redirect to return URL or home page after successful login
      if (returnUrl) {
        // Use setTimeout to ensure auth state is updated before navigation
        setTimeout(() => {
          navigate(returnUrl);
        }, 100);
      } else {
        navigate('/home');
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Azure AD sign-in failed');
    } finally {
      setAuthLoading(null);
    }
  };

  const handleBackToMain = () => {
    setShowEmailForm(false);
    setErrorMessage('');
  };

  // Floating animation keyframes
  const floatingAnimation = {
    '@keyframes float': {
      '0%, 100%': { transform: 'translateY(0px)' },
      '50%': { transform: 'translateY(-20px)' }
    },
    '@keyframes pulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.7 }
    },
    '@keyframes rotate': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' }
    }
  };

  if (showEmailForm) {
    return (
      <div
        className="login-screen"
        style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          fontFamily: theme.fonts.primary,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Animated Background Elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '100px',
          height: '100px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }} />
        <div style={{
          position: 'absolute',
          top: '60%',
          right: '15%',
          width: '150px',
          height: '150px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '50%',
          animation: 'float 8s ease-in-out infinite reverse',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '20%',
          left: '20%',
          width: '80px',
          height: '80px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '50%',
          animation: 'pulse 4s ease-in-out infinite',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.15)'
        }} />

        <div style={{
          width: '100%',
          maxWidth: '450px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '24px',
          padding: '40px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          position: 'relative',
          zIndex: 10,
          transform: `translate(${(mousePosition.x - window.innerWidth / 2) * 0.02}px, ${(mousePosition.y - window.innerHeight / 2) * 0.02}px)`,
          transition: 'transform 0.1s ease-out'
        }}>
          <button
            onClick={handleBackToMain}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.textSecondary,
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              cursor: 'pointer',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.primary}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
          >
            ← Back
          </button>

          <h2 style={{
            color: theme.colors.text,
            fontSize: theme.typography.h2.fontSize,
            fontWeight: theme.typography.h2.fontWeight,
            fontFamily: theme.fonts.display,
            lineHeight: theme.typography.h2.lineHeight,
            letterSpacing: theme.typography.h2.letterSpacing,
            marginBottom: '8px',
            marginTop: '0'
          }}>
            {isSignUp ? 'Create your account' : 'Welcome back'}
          </h2>

          <p style={{
            color: theme.colors.textSecondary,
            fontSize: theme.typography.body.fontSize,
            fontWeight: theme.typography.body.fontWeight,
            fontFamily: theme.fonts.secondary,
            lineHeight: theme.typography.body.lineHeight,
            letterSpacing: theme.typography.body.letterSpacing,
            marginBottom: '32px',
            marginTop: '0'
          }}>
            {isSignUp ? 'Enter your details to get started' : 'Sign in to continue'}
          </p>

          <form onSubmit={handleEmailSignIn}>
            {isSignUp && (
              <input
                type="text"
                placeholder="Full Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  border: `2px solid ${theme.colors.border}`,
                  borderRadius: '12px',
                  fontSize: theme.typography.body.fontSize,
                  fontWeight: theme.typography.body.fontWeight,
                  fontFamily: theme.fonts.secondary,
                  lineHeight: theme.typography.body.lineHeight,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  color: theme.colors.text,
                  marginBottom: '16px',
                  boxSizing: 'border-box',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(10px)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = theme.colors.primary;
                  e.target.style.boxShadow = `0 0 0 3px ${theme.colors.primary}20`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = theme.colors.border;
                  e.target.style.boxShadow = 'none';
                }}
                required
              />
            )}

            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: `2px solid ${theme.colors.border}`,
                borderRadius: '12px',
                fontSize: theme.typography.body.fontSize,
                fontWeight: theme.typography.body.fontWeight,
                fontFamily: theme.fonts.secondary,
                lineHeight: theme.typography.body.lineHeight,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                color: theme.colors.text,
                marginBottom: '16px',
                boxSizing: 'border-box',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = theme.colors.primary;
                e.target.style.boxShadow = `0 0 0 3px ${theme.colors.primary}20`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = theme.colors.border;
                e.target.style.boxShadow = 'none';
              }}
              required
            />

            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: `2px solid ${theme.colors.border}`,
                borderRadius: '12px',
                fontSize: theme.typography.body.fontSize,
                fontWeight: theme.typography.body.fontWeight,
                fontFamily: theme.fonts.secondary,
                lineHeight: theme.typography.body.lineHeight,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                color: theme.colors.text,
                marginBottom: '16px',
                boxSizing: 'border-box',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = theme.colors.primary;
                e.target.style.boxShadow = `0 0 0 3px ${theme.colors.primary}20`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = theme.colors.border;
                e.target.style.boxShadow = 'none';
              }}
              required
            />

            {errorMessage && (
              <div style={{
                color: theme.colors.error,
                fontSize: theme.typography.caption.fontSize,
                fontWeight: theme.typography.caption.fontWeight,
                fontFamily: theme.fonts.secondary,
                lineHeight: theme.typography.caption.lineHeight,
                letterSpacing: theme.typography.caption.letterSpacing,
                marginBottom: '16px',
                padding: '12px 16px',
                backgroundColor: `${theme.colors.error}10`,
                borderRadius: '12px',
                border: `1px solid ${theme.colors.error}20`,
                backdropFilter: 'blur(10px)',
                animation: 'pulse 2s ease-in-out'
              }}>
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '16px 20px',
                background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%)`,
                color: theme.colors.primaryText,
                border: 'none',
                borderRadius: '12px',
                fontSize: theme.typography.button.fontSize,
                fontWeight: theme.typography.button.fontWeight,
                fontFamily: theme.fonts.primary,
                lineHeight: theme.typography.button.lineHeight,
                letterSpacing: theme.typography.button.letterSpacing,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
                }
              }}
            >
              {isLoading ? 'Loading...' : (isSignUp ? 'Create Account' : 'Sign In')}
            </button>
          </form>

          <button
            onClick={() => setIsSignUp(!isSignUp)}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.primary,
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              lineHeight: theme.typography.body.lineHeight,
              cursor: 'pointer',
              marginTop: '20px',
              textDecoration: 'underline',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.secondary}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.primary}
          >
            {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="login-screen"
      style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: theme.fonts.primary,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated Background Elements */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '10%',
        width: '120px',
        height: '120px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '50%',
        animation: 'float 6s ease-in-out infinite',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)'
      }} />
      <div style={{
        position: 'absolute',
        top: '60%',
        right: '15%',
        width: '180px',
        height: '180px',
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '50%',
        animation: 'float 8s ease-in-out infinite reverse',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '20%',
        left: '20%',
        width: '100px',
        height: '100px',
        background: 'rgba(255, 255, 255, 0.08)',
        borderRadius: '50%',
        animation: 'pulse 4s ease-in-out infinite',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.15)'
      }} />
      <div style={{
        position: 'absolute',
        top: '30%',
        right: '30%',
        width: '60px',
        height: '60px',
        background: 'rgba(255, 255, 255, 0.06)',
        borderRadius: '50%',
        animation: 'rotate 20s linear infinite',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }} />

      {/* Font Size Selector - Top Right */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 10,
      }}>
        <FontSizeSelector compact />
      </div>

      <div
        className="login-center-card"
        style={{
          width: '100%',
          maxWidth: '450px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '28px',
          padding: '48px',
          boxShadow: '0 25px 80px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(25px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          textAlign: 'center',
          position: 'relative',
          zIndex: 10,
          transform: `translate(${(mousePosition.x - window.innerWidth / 2) * 0.01}px, ${(mousePosition.y - window.innerHeight / 2) * 0.01}px)`,
          transition: 'transform 0.1s ease-out'
        }}
      >
        {/* Logo */}
        <div style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <img
            src={fitzaaLogo}
            alt="Fitzaa Logo"
            style={{
              width: '100px',
              height: '100px',
              borderRadius: '20px',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
              display: 'block'
            }}
          />
        </div>

        {/* Brand Title */}
        <h1 style={{
          color: theme.colors.text,
          fontSize: '2.5rem',
          fontWeight: '800',
          fontFamily: theme.fonts.display,
          marginBottom: '8px',
          marginTop: '0',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          letterSpacing: '-0.02em'
        }}>
          fitzaa
        </h1>

        <p style={{
          color: theme.colors.textSecondary,
          fontSize: theme.typography.body.fontSize,
          fontWeight: theme.typography.body.fontWeight,
          fontFamily: theme.fonts.secondary,
          lineHeight: theme.typography.body.lineHeight,
          letterSpacing: theme.typography.body.letterSpacing,
          marginBottom: '40px',
          marginTop: '0',
          opacity: 0.8
        }}>
          wellness and fitness challenger!
        </p>

        {/* Sign In Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <button
            onClick={handleAzureADSignIn}
            disabled={authLoading !== null}
            style={{
              width: '100%',
              padding: '16px 20px',
              background: 'linear-gradient(135deg, #232b5d 0%, #3f51b5 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              fontSize: `${Math.max(parseInt(theme.typography.button.fontSize) - 3, 12)}px`,
              fontWeight: theme.typography.button.fontWeight,
              fontFamily: theme.fonts.primary,
              lineHeight: theme.typography.button.lineHeight,
              letterSpacing: theme.typography.button.letterSpacing,
              cursor: authLoading !== null ? 'not-allowed' : 'pointer',
              opacity: authLoading !== null ? 0.7 : 1,
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              boxShadow: '0 4px 15px rgba(35, 43, 93, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (authLoading === null) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(35, 43, 93, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (authLoading === null) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(35, 43, 93, 0.3)';
              }
            }}
          >
            {authLoading === 'azure' ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'rotate 1s linear infinite'
                }} />
                Signing in...
              </>
            ) : (
              <>
                <img
                  src="https://www.pngmart.com/files/23/Azure-Logo-PNG-Photo.png"
                  alt="Azure AD"
                  style={{
                    width: '28px',
                    height: '28px',
                    marginRight: '8px',
                    objectFit: 'contain'
                  }}
                />
                Continue with SSO
              </>
            )}
          </button>

          <button
            onClick={handleGoogleSignIn}
            disabled={authLoading !== null}
            style={{
              width: '100%',
              padding: '16px 20px',
              background: 'white',
              color: '#333',
              border: '1px solid #e0e0e0',
              borderRadius: '16px',
              fontSize: `${Math.max(parseInt(theme.typography.button.fontSize) - 3, 12)}px`,
              fontWeight: theme.typography.button.fontWeight,
              fontFamily: theme.fonts.primary,
              lineHeight: theme.typography.button.lineHeight,
              letterSpacing: theme.typography.button.letterSpacing,
              cursor: authLoading !== null ? 'not-allowed' : 'pointer',
              opacity: authLoading !== null ? 0.7 : 1,
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (authLoading === null) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                e.currentTarget.style.borderColor = '#d0d0d0';
              }
            }}
            onMouseLeave={(e) => {
              if (authLoading === null) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.borderColor = '#e0e0e0';
              }
            }}
          >
            {authLoading === 'google' ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'rotate 1s linear infinite'
                }} />
                Signing in...
              </>
            ) : (
              <>
                <img
                  src="https://cdn1.iconfinder.com/data/icons/google-s-logo/150/Google_Icons-09-512.png"
                  alt="Google"
                  style={{
                    width: '28px',
                    height: '28px',
                    marginRight: '8px',
                    objectFit: 'contain'
                  }}
                />
                Continue with Google
              </>
            )}
          </button>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            margin: '32px 0',
            color: theme.colors.textSecondary,
            fontSize: theme.typography.caption.fontSize,
            fontFamily: theme.fonts.secondary,
          }}>
            <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)' }}></div>
            <span style={{ padding: '0 20px', opacity: 0.7 }}>or</span>
            <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)' }}></div>
          </div>

          <button
            onClick={() => setShowEmailForm(true)}
            style={{
              width: '100%',
              padding: '16px 20px',
              backgroundColor: 'transparent',
              color: theme.colors.primary,
              border: `2px solid ${theme.colors.primary}`,
              borderRadius: '16px',
              fontSize: theme.typography.button.fontSize,
              fontWeight: theme.typography.button.fontWeight,
              fontFamily: theme.fonts.primary,
              lineHeight: theme.typography.button.lineHeight,
              letterSpacing: theme.typography.button.letterSpacing,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.backgroundColor = `${theme.colors.primary}10`;
              e.currentTarget.style.boxShadow = `0 8px 25px ${theme.colors.primary}20`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <span style={{ fontSize: '20px', marginRight: '8px' }}>✉️</span>
            Sign in with Email
          </button>
        </div>

        {errorMessage && (
          <div style={{
            color: theme.colors.error,
            fontSize: theme.typography.caption.fontSize,
            fontWeight: theme.typography.caption.fontWeight,
            fontFamily: theme.fonts.secondary,
            lineHeight: theme.typography.caption.lineHeight,
            letterSpacing: theme.typography.caption.letterSpacing,
            marginTop: '20px',
            padding: '12px 16px',
            backgroundColor: `${theme.colors.error}10`,
            borderRadius: '12px',
            border: `1px solid ${theme.colors.error}20`,
            backdropFilter: 'blur(10px)',
            animation: 'pulse 2s ease-in-out'
          }}>
            {errorMessage}
          </div>
        )}

        {/* Footer */}
        <div style={{
          marginTop: '40px',
          paddingTop: '20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
          opacity: 0.7
        }}>
          <p style={{
            color: theme.colors.textSecondary,
            fontSize: '12px',
            margin: 0,
            fontFamily: theme.fonts.secondary
          }}>
            powered by Pasutech LLC © 2025
          </p>
        </div>
      </div>

      {/* Add CSS animations */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          @keyframes rotate {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default LoginScreen; 